import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * PageMeta — per-route SEO + social meta helper.
 *
 * Drops Helmet tags for title, description, OG, Twitter, canonical, and
 * (optionally) JSON-LD structured data into the head. Every public route
 * should mount one of these so search and link unfurlers see something
 * meaningful instead of the SPA shell title.
 *
 * Defaults are tuned for the Nokia death-economy positioning — never
 * generic "social media app" boilerplate. Routes override what they
 * need; everything else inherits the home defaults.
 *
 * Usage:
 *   <PageMeta
 *     title="Graveyard"
 *     description="The anonymous registry of users wiped from wipe.haus."
 *     path="/graveyard"
 *   />
 *
 * For posts/profiles you can pass a `jsonLd` object that will be
 * serialized into a <script type="application/ld+json"> tag.
 */

interface PageMetaProps {
  /** Page title — appended with " — wipe.haus" suffix */
  title?: string;
  /** Meta description / OG description / Twitter description */
  description?: string;
  /** Absolute path (e.g. "/graveyard") used for canonical + og:url */
  path?: string;
  /** OG image URL (absolute or root-relative); defaults to wipe.haus/og.png */
  image?: string;
  /** OG type — "website" by default, "article" for posts */
  ogType?: 'website' | 'article' | 'profile';
  /** JSON-LD structured data object — serialized inline */
  jsonLd?: Record<string, any>;
  /** Set true on /login, /signup, etc. so search engines don't index them */
  noindex?: boolean;
}

const SITE = 'wipe.haus';
const SITE_DESCRIPTION =
  'A time-economy social microblog where every post, follow, and vote costs seconds from your wallet. Everything dies on a deadline unless someone keeps it alive. We delete your data on purpose.';
const DEFAULT_IMAGE = 'https://wipe.haus/og.png';
const ORIGIN = 'https://wipe.haus';

function PageMeta({
  title,
  description = SITE_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  ogType = 'website',
  jsonLd,
  noindex = false,
}: PageMetaProps) {
  const fullTitle = title ? `${title} — ${SITE}` : `${SITE} — time-economy social`;
  const canonical = `${ORIGIN}${path}`;
  const absImage = image.startsWith('http') ? image : `${ORIGIN}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* OpenGraph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={absImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Site-wide JSON-LD that should appear on the homepage. Declares the
 * WebSite entity so Google Knowledge Graph and link previewers have a
 * canonical name + url + search action.
 */
export const SITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'wipe.haus',
  url: ORIGIN,
  description: SITE_DESCRIPTION,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${ORIGIN}/?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export default PageMeta;
